import {
  ClaimPriorityCode,
  ClaimTypeCode,
  type CreateReference,
} from '@quromedical/fhir-common';
import { z } from 'zod';
import {
  type ChargeItemDefinitionMoney,
  ChargeItemDefinitionMoneySchema,
} from '../charge-item-definition/schema';
import {
  type ClaimResponseModelGetResponse,
  ClaimResponseModelGetResponseSchema,
} from '../claim-response';
import { ClaimTemplateChargeLineQuantitySchema } from '../claim-template/schema';
import {
  DisplayReferenceSchema,
  UUIDSchema,
  VersionedReferenceSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';

export type ClaimStatus = fhir4.Claim['status'];

type Count = number;

export interface ClaimChargeLineQuantityItem {
  /**
   * In the case of a scheme related charge this would be the tariff code
   */
  code: string;
  amount: ChargeItemDefinitionMoney;
  name: string;
}

export interface ClaimChargeLineQuantity {
  chargeItem: CreateReference;
  chargeLine: ClaimChargeLineQuantityItem;
  quantity: Count;
}

const ChargeLineQuantityItemSchema = z.object({
  code: z.string(),
  amount: ChargeItemDefinitionMoneySchema,
  name: z.string(),
});

export const ChargeLineQuantitySchema = z.object({
  chargeItem: DisplayReferenceSchema,
  chargeLine: ChargeLineQuantityItemSchema,
  quantity: z.number(),
});

export const ClaimTypeCodeSchema = z.nativeEnum(ClaimTypeCode);

export const SupportedClaimStatus = [
  'active',
  'cancelled',
  'draft',
  'entered-in-error',
] as const;

export const ClaimStatusSchema = z.enum(SupportedClaimStatus);

export const ClaimPriorityCodeSchema = z.nativeEnum(ClaimPriorityCode);

export const CoverageSchema = z.object({
  reference: VersionedReferenceSchema,
});

export const ClaimSchema = z.object({
  type: ClaimTypeCodeSchema,
  status: ClaimStatusSchema,
  created: z.string(),
  provider: DisplayReferenceSchema,
  coverage: CoverageSchema,
  patient: DisplayReferenceSchema,
  priority: ClaimPriorityCodeSchema,
  admission: DisplayReferenceSchema,
  chargeLines: ChargeLineQuantitySchema.array(),
  invoiceNumber: z.string().optional(),
});

/**
 * A claim references a single patient, coverage, and admission with a list of different charge
 * lines embedded within the claim as a snapshot from the time of the claim creation
 *
 * By design, a claim should be created automatically when a patient is onboarded
 */
export type Claim = z.infer<typeof ClaimSchema>;

export interface ClaimWithResponses extends PartialClaim {
  responses: ClaimResponseModelGetResponse[];
}

export const PartialClaimSchema = ClaimSchema.partial();
export const ClaimGetResponseSchema = PartialClaimSchema.extend({
  responses: ClaimResponseModelGetResponseSchema.array(),
});

export const PartialClaimWithIdSchema = PartialClaimSchema.extend({
  id: UUIDSchema,
});

export const ClaimGetResponseWithIdSchema = ClaimGetResponseSchema.extend({
  id: UUIDSchema,
});

export const ClaimResponseInputSchema = z.object({
  id: UUIDSchema,
  responseId: UUIDSchema.optional(),
});

export const ClaimGetAllResponseSchema = PaginatedBundleSchema.extend({
  claims: PartialClaimWithIdSchema.array(),
});

export const ClaimGetAllParams = PaginationSchema.extend({
  identifier: z.string().optional(),
  status: ClaimStatusSchema.optional(),
  patientName: z.string().optional(),
});

export type ClaimGetResponse = z.infer<typeof ClaimGetResponseWithIdSchema>;

export type ClaimSearchParams = z.infer<typeof ClaimGetAllParams>;

export type ClaimGetAllResponse = z.infer<typeof ClaimGetAllResponseSchema>;

export type PartialClaim = z.infer<typeof PartialClaimSchema>;

export type ClaimGetAllParams = z.infer<typeof ClaimGetAllParams>;

export const ClaimUpdateRequestSchema = z.object({
  id: UUIDSchema,
  chargeLines: ClaimTemplateChargeLineQuantitySchema.array(),
});
/**
 * A claim can only be updated as the creation of draft claims happens automatically when a patient
 * is admitted and the admission is created
 */
export type ClaimUpdateRequest = z.infer<typeof ClaimUpdateRequestSchema>;

export const ClaimUpdateResponseSchema = PartialClaimSchema.extend({
  id: UUIDSchema,
});
export type ClaimUpdateResponse = z.infer<typeof ClaimUpdateResponseSchema>;

export const SubmitClaimRequestSchema = z.object({ id: UUIDSchema });
export type SubmitClaimRequest = z.infer<typeof SubmitClaimRequestSchema>;

export type SubmitClaimResponse = PartialClaim;
